import { createContext, useContext, type ReactNode } from 'react'
import type { TmsInvoiceCreateRow, TmsLoadRow } from '@numeo/types-main-web-app'
import type { ChatCard } from './cardResults'
import { invoiceCellOf } from './format'

/** The invoices this chat made: its invoice number, or "Invoiced" when the answer named none, by load id and by load number. */
interface ThreadInvoices {
    byLoadId: ReadonlyMap<string, string>
    /** Only for a row that carries no load id (an older answer): a load number is not unique, two loads can show the same one. */
    byNumber: ReadonlyMap<string, string>
}

const NONE: ThreadInvoices = { byLoadId: new Map(), byNumber: new Map() }

/**
 * The invoices this chat made. Every card is a snapshot, so a Ready item or a load row drawn before the invoice still offered
 * "Invoice"; with this, a load the chat invoiced reads as invoiced wherever it shows, and is never offered again. Matched by the
 * load's id: the board's number (`sourceRateConfId`, else the first reference) can be the same on two loads.
 */
export function threadInvoicesOf(cards: ChatCard[]): ThreadInvoices {
    const byLoadId = new Map<string, string>()
    const byNumber = new Map<string, string>()
    for (const { view } of cards) {
        if (view.view !== 'writes' || view.card !== 'tms_invoices.create') continue
        for (const row of view.rows as TmsInvoiceCreateRow[]) {
            if (row.outcome !== 'updated') continue
            const invoice = row.invoice_number ?? 'Invoiced'
            if (row.load_id) byLoadId.set(row.load_id, invoice)
            if (row.load_number) byNumber.set(row.load_number, invoice)
        }
    }
    return { byLoadId, byNumber }
}

const ThreadInvoicesContext = createContext<ThreadInvoices>(NONE)

export const ThreadInvoicesProvider = ({ invoiced, children }: { invoiced: ThreadInvoices; children: ReactNode }) => (
    <ThreadInvoicesContext.Provider value={invoiced}>{children}</ThreadInvoicesContext.Provider>
)

/** The invoice the chat made for this load, if it made one: by its id, and by its number only when the row has no id. */
export const useInvoicedAs = (loadId: string | null | undefined, loadNumber: string | null | undefined): string | undefined => {
    const invoiced = useContext(ThreadInvoicesContext)
    if (loadId) return invoiced.byLoadId.get(loadId)
    return loadNumber ? invoiced.byNumber.get(loadNumber) : undefined
}

/** The invoice the chat made for a row's load (by its id, else its number), for a list that asks it once per row. */
export const useInvoicedLookup = (): ((row: { load_id?: string; load_number?: string | null; load_numbers?: string[] }) => string | undefined) => {
    const invoiced = useContext(ThreadInvoicesContext)
    return (row) => {
        if (row.load_id) return invoiced.byLoadId.get(row.load_id)
        const number = row.load_number ?? row.load_numbers?.[0]
        return number ? invoiced.byNumber.get(number) : undefined
    }
}

/** A load's invoice cell: the invoice the chat made for it, else what its row was drawn with. */
export const InvoiceCell = ({ row }: { row: TmsLoadRow }) => {
    const invoicedAs = useInvoicedAs(row.load_id, row.load_number)
    return <>{invoicedAs && row.status === 'delivered' ? invoicedAs : invoiceCellOf(row)}</>
}
